import { z } from "zod";

const customerIdPattern = /^[A-Za-z0-9-]+$/;

export interface UserFormModalValidationMessages {
  customerIdRequired: string;
  customerIdInvalid: string;
  firstNameRequired: string;
  lastNameRequired: string;
  emailRequired: string;
  emailInvalid: string;
  confirmEmailRequired: string;
  confirmEmailInvalid: string;
  emailsDoNotMatch: string;
  roleRequired: string;
}

// English fallbacks so a dictionary missing a key still shows a readable
// message instead of an empty one while the form stays invalid.
export const defaultUserFormModalValidationMessages: UserFormModalValidationMessages =
  {
    customerIdRequired: "Customer ID is required",
    customerIdInvalid:
      "Customer ID can only contain letters, numbers, and hyphens (-)",
    firstNameRequired: "First name is required",
    lastNameRequired: "Last name is required",
    emailRequired: "Email is required",
    emailInvalid: "Please enter a valid email address",
    confirmEmailRequired: "Confirm email is required",
    confirmEmailInvalid: "Please enter a valid email address",
    emailsDoNotMatch: "Email addresses don't match",
    roleRequired: "Please select a role",
  };

function withFallbacks(
  overrides: Partial<UserFormModalValidationMessages>
): UserFormModalValidationMessages {
  const messages = { ...defaultUserFormModalValidationMessages };
  for (const key of Object.keys(
    messages
  ) as (keyof UserFormModalValidationMessages)[]) {
    messages[key] = overrides[key] || messages[key];
  }
  return messages;
}

export function createUserFormModalSchema(
  overrides: Partial<UserFormModalValidationMessages> = {}
) {
  const messages = withFallbacks(overrides);
  return z
    .object({
      // Admin-supplied on both add and edit — see ADR-0027. Disabled/read-only
      // once set (edit mode), but required input when adding a new user.
      customerId: z
        .string()
        .refine((value) => value.trim().length > 0, {
          message: messages.customerIdRequired,
        })
        .refine(
          (value) => value.trim().length === 0 || customerIdPattern.test(value),
          {
            message: messages.customerIdInvalid,
          }
        ),
      firstName: z.string().min(1, messages.firstNameRequired),
      lastName: z.string().min(1, messages.lastNameRequired),
      email: z
        .string()
        .min(1, messages.emailRequired)
        .email(messages.emailInvalid),
      confirmEmail: z
        .string()
        .min(1, messages.confirmEmailRequired)
        .email(messages.confirmEmailInvalid),
      // No role is pre-selected: optional here, required via the refine below,
      // so an untouched form doesn't already satisfy validation.
      role: z
        .enum(["Administrator", "Sub Administrator", "Employee", "Viewer"])
        .optional(),
    })
    .refine((data) => data.email === data.confirmEmail, {
      message: messages.emailsDoNotMatch,
      path: ["confirmEmail"],
    })
    .refine((data) => data.role !== undefined, {
      message: messages.roleRequired,
      path: ["role"],
    });
}

export type UserFormModalData = z.infer<
  ReturnType<typeof createUserFormModalSchema>
>;
